import { Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { Subject } from 'rxjs';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { CuboidState3D } from '../../state/cuboid-state-3d';
import { ensureKeyLocation } from '../../utils/point-cloud-interpolation-helper';
import { AbstractPointCloudTool } from '../abstract-point-cloud-tool';
import { ShapeTool } from './abstract-shape-tool-helper';
import { ArrowCuboidToolHelper } from './arrow-cuboid-tool-helper';

@Injectable()
export class CuboidTool3D extends AbstractPointCloudTool implements ShapeTool {
  readonly name = 'Cuboid';

  drawShapeFinished$ = new Subject<void>();

  private redrawCuboidStore?: Store<CuboidState3D>;
  private cuboidBeforeRedraw?: CuboidState3D;
  private arrowCuboidToolHelper?: ArrowCuboidToolHelper;

  override async onActivate() {
    await super.onActivate();

    // Clearing the selection before drawing a shape prevents layout shifts
    // that change the container point position mid shape creation
    this.shellConnector.modifyShapeSelection([]);

    this.onEvent('container', 'click', this.onContainerClick, {
      evenWithRegisteredHelper: true,
    });
  }

  buildShape(initialValues: CuboidState3D) {
    return initialValues;
  }

  startRedraw(cuboidStore: Store<CuboidState3D>) {
    this.cuboidBeforeRedraw = cuboidStore.state();
    this.redrawCuboidStore = cuboidStore;
    ensureKeyLocation(
      cuboidStore('key_locations'),
      this.store.state().render.frameNumber,
    )('visibility').set(0);
  }

  notifyDrawFinished(cuboid: CuboidState3D | undefined) {
    if (this.redrawCuboidStore) {
      this.updateShape(cuboid);
    } else if (cuboid) {
      this.addShape(cuboid);
    }
    this.drawShapeFinished$.next();
  }

  override onCancel(eventTrackingValue: EventTrackingValue) {
    super.onCancel(eventTrackingValue);
    this.notifyDrawFinished(undefined);
  }

  private onContainerClick() {
    if (!this.arrowCuboidToolHelper) {
      this.arrowCuboidToolHelper = new ArrowCuboidToolHelper(
        this,
        this.injector,
        this.store('render')('inProgressCuboid'),
      );
      void this.arrowCuboidToolHelper.completion.finally(() => {
        this.arrowCuboidToolHelper = undefined;
      });
      this.registerHelper(this.arrowCuboidToolHelper);

      return;
    }

    this.arrowCuboidToolHelper.handleClick();
  }

  private addShape(inProgressCuboid: CuboidState3D) {
    this.shellConnector.createShape(inProgressCuboid, 'arrow-cuboid');
    this.store('selectedTool').set('Edit');
  }

  private updateShape(inProgressCuboid: CuboidState3D | undefined) {
    const redrawCuboidStore = this.redrawCuboidStore;
    const cuboidBeforeRedraw = this.cuboidBeforeRedraw;
    assert(
      redrawCuboidStore && cuboidBeforeRedraw,
      'This should only be called when redrawing',
    );

    this.store.batch(() => {
      redrawCuboidStore('key_locations').set(cuboidBeforeRedraw.key_locations);
      if (inProgressCuboid) {
        this.setRedrawnKeyLocation(inProgressCuboid.key_locations[0]);
      }
    });
    this.shellConnector.modifyShapeSelection([cuboidBeforeRedraw.index]);

    if (inProgressCuboid) {
      this.shellConnector.pushAndTrack({
        id: 'edit_shape_points',
        bucket: 'adjustment',
        shapes: [{ ...inProgressCuboid, index: cuboidBeforeRedraw.index }],
        method: 'redraw',
        source: 'edit_tool',
      });
    }
    this.redrawCuboidStore = undefined;
    this.cuboidBeforeRedraw = undefined;
  }

  private setRedrawnKeyLocation(location: CuboidLocationState3D) {
    assert(this.redrawCuboidStore, 'This should only be called when redrawing');

    const { frameNumber } = this.store.state().render;
    const locationStore = ensureKeyLocation(
      this.redrawCuboidStore('key_locations'),
      frameNumber,
    );
    locationStore.set(location);
  }
}
